import React, { useState, useEffect } from 'react';

import { Card } from 'antd';
import Axios from 'axios';

import styled from 'styled-components';

import { openDocument } from '@libs/NwPrivateFile';
import { isMediaPreviewable } from '@libs/NwUtils';

import { useProjectContext, useProjectContextDispatch } from '@contexts/ProjectContext';
import { useStateValue } from '@contexts/StateProvider';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import { forceReloadProject, onGetOrderedEventList } from '@hooks/API/Project/UseProject';
import { useAdjustTagUsageCount } from '@hooks/CachedLists/UseTagCatalog';

import Document from '@components/ContactDetailsDocuments/Document';
import CreatedBy from '@components/CreatedBy/CreatedBy';
import EmptyState from '@components/Gui/EmptyState';
import FlexContainer from '@components/Gui/FlexContainer';
import { Loading } from '@components/Gui/Loading';
import { NwCard } from '@components/Gui/NwCard/NwCard';
import NwDrawer from '@components/Gui/NwDrawer';
import NwDropDownMenu from '@components/Gui/NwDropDownMenu';
import NwIcon from '@components/Gui/NwIcon';
import NwUpload from '@components/Gui/NwUpload';
import MediaDrawerPreviewOnly from '@components/MediaDrawer/MediaDrawerPreviewOnly';
import AttachmentDetailsForm from '@components/Project/ProjectDocuments/AttachmentDetailsForm';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const MiniContent = styled.div`
    font-size: 12px;
    padding-left: 10px;
    .model-name {
        font-weight: bold;
    }
    .document-row {
        display: flex;
        flex-direction: row;
        cursor: pointer;
        user-select: none;
        .document-image {
            font-size: 20px;
            margin-left: 5px;
        }
        .document-title {
            margin: auto 0 auto 10px;
        }
    }
`;

const Wrapper = styled.div`
    margin-bottom: 1rem;
    .ant-card-body-content {
        padding: 0 !important;
    }
`;

const GridCard = styled(Card.Grid)`
    float: left; //added because ant removed it to use flex- to update later
    width: 25%;
    height: ${(props) => (props.vertical ? 'auto' : '8rem')};
    @media only screen and (max-width: 1600px) {
        width: 33.33%;
    }
    @media only screen and (max-width: 1200px) {
        width: 50%;
    }
    @media only screen and (max-width: 600px) {
        width: 100%;
    }
    width: ${(props) => props.vertical && '100% !important'};

    .ant-list-item-meta {
        width: 100%;
    }
`;

const DocumentBox = styled.div`
    width: 100%;
    margin-bottom: 24px;

    &:last-child {
        margin-bottom: 0;
    }

    .header {
        text-transform: uppercase;
        font-weight: 500;
        font-size: ${(props) => props.theme.FontSizeBase};
        padding: 1rem 2rem;
        margin-bottom: 0;
    }
`;

const groupDocumentsByModel = (documents, filterModel) => {
    const groupedDocuments = {};
    documents.forEach((document) => {
        if (!document.ContactID || document.ContactID === filterModel) {
            return;
        }
        if (groupedDocuments[document.ContactID]) {
            groupedDocuments[document.ContactID].push(document);
            return;
        }
        groupedDocuments[document.ContactID] = [document];
    });
    return groupedDocuments;
};

const ProjectDocuments = ({ modelId, vertical }) => {
    const { projectId, projectData: currentProject } = useProjectContext();
    const projectDispatch = useProjectContextDispatch();
    const adjustTagUsageCount = useAdjustTagUsageCount();
    const [{ areaContext }] = useStateValue();
    const [documentId, setDocumentId] = useState(null);
    const [isDocumentDrawer, setIsDocumentDrawer] = useState(false);
    const [isDocumentPreview, setIsDocumentPreview] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [documentsCount, setDocumentsCount] = useState(0);
    const [events, setEvents] = useState([]);

    const [touchForm, setTouchForm] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (currentProject) {
            setDocuments(currentProject.Documents);
            const projectEvents = onGetOrderedEventList(currentProject, areaContext, false, false);
            setEvents([...projectEvents]);
        }
    }, [currentProject]);

    useEffect(() => {
        setDocumentsCount(documents.length);
    }, [documents]);

    const RenderUploadButton = () => (
        <NwUpload
            maxSize={20}
            showList={false}
            types={[
                'document',
                'image',
                'data-document',
                'office-document',
                'archive-document',
                'text-document',
                'video',
            ]}
            onUploadStart={() => {}}
            onUploadEnd={async () => {
                const updatedProject = await forceReloadProject(projectId);
                projectDispatch({ type: 'UpdateProjectData', projectData: updatedProject });
                // Update local documents state immediately
                if (updatedProject?.Documents && updatedProject.Documents.length > 0) {
                    setDocuments(updatedProject.Documents);
                    // Open drawer for the newly uploaded document (first in array)
                    setDocumentId(updatedProject.Documents[0].ID);
                    setIsDocumentDrawer(true);
                }
            }}
            endpoint={`documents/uploadsingle/1?projectID=${projectId}`}
        />
    );

    const handleDocumentDownload = (document) => {
        openDocument(document, { download: true });
    };

    const onDeleteDocument = async (document) => {
        try {
            await Axios.delete(`/documents/?ids=${document.ID}`);
            // il documento eliminato non usa piu' i suoi tag
            (document.Tags || []).forEach((tag) => adjustTagUsageCount(tag.ID, -1));
            const updatedProject = await forceReloadProject(projectId);
            projectDispatch({ type: 'UpdateProjectData', projectData: updatedProject });
            // Update local documents state immediately
            if (updatedProject?.Documents) {
                setDocuments(updatedProject.Documents);
            }
        } catch (error) {
            console.error('Errore', error);
        }
    };

    const onCloseDrawer = () => {
        setTouchForm(false);
        setDocumentId();
        setIsDocumentDrawer(false);
    };

    //const documents = currentProject?[...currentProject.Documents]:[];
    const generalDocuments = documents.filter((d) => d.ContactID === null);
    const selectedModelDocuments = documents.filter((d) => d.ContactID === modelId);
    const groupedDocuments = groupDocumentsByModel(documents, modelId);

    const document = documents.find((item) => item.ID === documentId);

    const getDescription = (document) => {
        if (document.IsHidden) {
            return (
                <>
                    <span className="doc-hidden">
                        <NwIcon icon={byPrefixAndName.fasl['eye-slash']} /> Hidden from models/talents
                    </span>
                    <CreatedBy object={document} short textonly />
                </>
            );
        }
        return <CreatedBy object={document} short textonly />;
    };

    const handleDocumentEdit = (document) => {
        setDocumentId(document.ID);
        setIsDocumentDrawer(true);
    };

    const handleDocumentPreview = (document) => {
        if (isMediaPreviewable(document.Extension)) {
            setDocumentId(document.ID);
            setIsDocumentPreview(true);
        } else {
            handleDocumentDownload(document);
        }
    };

    const closeDocumentPreview = () => {
        setDocumentId(null);
        setIsDocumentPreview(false);
    };

    const onEditAttachment = async () => {
        onCloseDrawer();
        setLoading(true);
        const updatedProject = await forceReloadProject(projectId);
        projectDispatch({ type: 'UpdateProjectData', projectData: updatedProject });
        // Update local documents state immediately
        if (updatedProject?.Documents) {
            setDocuments(updatedProject.Documents);
        }
        setLoading(false);
    };

    const actions = (document) => {
        return (
            <NwDropDownMenu
                actions={[
                    {
                        name: '',
                        key: 'edit',
                        pinned: true,
                        icon: byPrefixAndName.fasl['pen'],
                        action: () => handleDocumentEdit(document),
                    },
                    {
                        name: '',
                        key: 'download',
                        pinned: true,
                        icon: byPrefixAndName.fasl['cloud-download'],
                        action: () => handleDocumentDownload(document),
                    },
                    {
                        name: '',
                        type: 'delete',
                        key: 'delete',
                        pinned: true,
                        icon: byPrefixAndName.fasl['trash-alt'],
                        confirmationPlacement: 'left',
                        action: () => onDeleteDocument(document),
                    },
                ]}
            />
        );
    };

    return (
        <Wrapper>
            <NwCard extra={RenderUploadButton()} title="Documents" padding="no-padding">
                {loading ? (
                    <Loading textBlack />
                ) : (
                    <>
                        {documents.length > 0 ? (
                            <FlexContainer fullWidth alignItems="flex-start" column>
                                {modelId && selectedModelDocuments.length > 0 && (
                                    <DocumentBox>
                                        <h3 className="header">{getContactName(modelId)}</h3>
                                        <div>
                                            {selectedModelDocuments.map((document) => (
                                                <GridCard key={`document-${document.ID}`} vertical={vertical}>
                                                    <Document
                                                        disablehover="true"
                                                        document={document}
                                                        onDocumentClick={handleDocumentPreview}
                                                        getDescription={getDescription}
                                                        showTag
                                                        actions={actions}
                                                        events={events}
                                                    />
                                                </GridCard>
                                            ))}
                                        </div>
                                    </DocumentBox>
                                )}
                                <DocumentBox>
                                    <h3 className="header">General documents</h3>
                                    <div>
                                        {generalDocuments.map((document) => (
                                            <GridCard key={`document-${document.ID}`} vertical={vertical}>
                                                <Document
                                                    disablehover="true"
                                                    document={document}
                                                    onDocumentClick={handleDocumentPreview}
                                                    getDescription={getDescription}
                                                    showTag
                                                    actions={actions}
                                                    events={events}
                                                />
                                            </GridCard>
                                        ))}
                                    </div>
                                </DocumentBox>
                                {!modelId &&
                                    Object.keys(groupedDocuments).map((modelId, index) => (
                                        <DocumentBox key={index}>
                                            <h3 className="header">{getContactName(+modelId)}</h3>
                                            <div>
                                                {groupedDocuments[modelId].map((document) => (
                                                    <GridCard key={`document-${document.ID}`} vertical={vertical}>
                                                        <Document
                                                            disablehover="true"
                                                            document={document}
                                                            onDocumentClick={handleDocumentPreview}
                                                            getDescription={getDescription}
                                                            showTag
                                                            actions={actions}
                                                            events={events}
                                                        />
                                                    </GridCard>
                                                ))}
                                            </div>
                                        </DocumentBox>
                                    ))}
                            </FlexContainer>
                        ) : (
                            <EmptyState message="No documents" />
                        )}
                    </>
                )}
            </NwCard>
            {isDocumentDrawer && document && (
                <NwDrawer touchForm={touchForm} title="Document Details" onClose={onCloseDrawer}>
                    <AttachmentDetailsForm
                        attachment={document}
                        project={currentProject}
                        events={events}
                        onSave={onEditAttachment}
                        onClose={onCloseDrawer}
                        onTouchForm={setTouchForm}
                    />
                </NwDrawer>
            )}
            {isDocumentPreview && document && (
                <MediaDrawerPreviewOnly mediaID={document.ID} onCloseDrawer={closeDocumentPreview} />
            )}
        </Wrapper>
    );
};

export default ProjectDocuments;
